import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, uploadFile, fileUrl, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, StatusBadge, fmtDate, fmtDateTime, nowRomeInput, EmptyState } from "@/components/shared";
import SignaturePad from "@/components/SignaturePad";
import MultiFilter from "@/components/MultiFilter";
import { ClipboardSignature, FileDown, Search, Plus, RotateCcw, ImagePlus, Loader2, X, Users2, ChevronLeft, ChevronRight, User, Package, ChevronsUpDown, ChevronUp, ChevronDown, PenLine, Upload, FileCheck2, Trash2, Pencil, RefreshCw } from "lucide-react";

const STATUS_LABEL = { attivo: "Attivo", restituito: "Restituito" };

const consegnaStatus = (a) =>
  a.has_signature ? { tone: "ok", label: "Firmato (tavoletta)" }
  : a.has_signed_document ? { tone: "ok", label: `Firmato a mano${a.signed_by ? " — " + a.signed_by : ""}` }
  : a.status === "attivo" ? { tone: "warn", label: "Da firmare" }
  : { tone: "muted", label: "Senza firma" };

const restituzioneStatus = (a) =>
  a.return_has_signature ? { tone: "ok", label: "Firmato (tavoletta)" }
  : a.has_return_signed_document ? { tone: "ok", label: `Firmato a mano${a.return_signed_by ? " — " + a.return_signed_by : ""}` }
  : a.return_skipped ? { tone: "muted", label: "Senza firma" }
  : { tone: "warn", label: "Da firmare" };

const SignChip = ({ s, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ${
    s.tone === "ok" ? "text-primary bg-primary/10" : s.tone === "warn" ? "text-warning bg-warning/12" : "text-muted-foreground bg-muted"}`}>
    {s.tone === "ok" && <FileCheck2 className="h-3.5 w-3.5" />}{s.label}
  </span>
);

function AssignmentTable({ rows: rowsIn, isAdmin, openPdf, openReturn, onSign, onUploadDoc, onRemoveDoc, onUploadReturnDoc, onRemoveReturnDoc, onEdit, onReset }) {
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [filters, setFilters] = useState({ asset_names: [], category_names: [], employee_name: [], kind: [], date: [], status: [] });
  const expanded = rowsIn.flatMap((a) => {
    const rows = [{ a, kind: "consegna", date: a.assigned_date }];
    if (a.has_return) rows.push({ a, kind: "restituzione", date: a.returned_date });
    return rows;
  });
  const val = {
    asset_names: (r) => r.a.asset_names || "—",
    category_names: (r) => r.a.category_names || "—",
    employee_name: (r) => r.a.employee_name || "—",
    kind: (r) => (r.kind === "consegna" ? "Consegna" : "Restituzione"),
    date: (r) => (r.date ? fmtDateTime(r.date) : "—"),
    status: (r) => (r.kind === "consegna" ? consegnaStatus(r.a) : restituzioneStatus(r.a)).label,
  };
  const opt = (k) => [...new Set(expanded.map(val[k]))].filter(Boolean).sort();
  const match = (arr, v) => arr.length === 0 || arr.includes(v);
  const rows = expanded
    .filter((r) => Object.keys(filters).every((k) => match(filters[k], val[k](r))))
    .sort((x, y) => {
      const k = sort.key;
      const raw = (r) => (k === "date" ? (r.date || "") : (val[k](r) || "").toLowerCase());
      const vx = raw(x), vy = raw(y);
      const cmp = vx < vy ? -1 : vx > vy ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  const toggle = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));
  const SortIcon = ({ k }) => sort.key !== k ? <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" /> : sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5 text-primary" /> : <ChevronDown className="h-3.5 w-3.5 text-primary" />;
  const Th = ({ k, children, className = "" }) => (
    <TableHead className={`label-caps ${className}`}>
      <div className="flex items-center justify-center gap-1">
        <button className="flex items-center gap-1 hover:text-primary" onClick={() => toggle(k)} data-testid={`cro-sort-${k}`}>
          {children} <SortIcon k={k} />
        </button>
        <MultiFilter label={children} options={opt(k)} value={filters[k]} onChange={(v) => setFilters({ ...filters, [k]: v })} testid={`cro-filter-${k}`} />
      </div>
    </TableHead>
  );
  return (
    <Card className="rounded-lg overflow-hidden">
      {rows.length ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <Th k="asset_names">Beni</Th>
                <Th k="category_names" className="hidden lg:table-cell">Categoria</Th>
                <Th k="employee_name">Assegnatario</Th>
                <Th k="kind">Verbale</Th>
                <Th k="date" className="hidden md:table-cell">Data</Th>
                <Th k="status">Firma</Th>
                <TableHead className="label-caps">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const a = r.a;
                const isConsegna = r.kind === "consegna";
                return (
                  <TableRow key={`${a.id}-${r.kind}`} data-testid={isConsegna ? `assignment-row-${a.id}` : `return-row-${a.id}`}>
                    <TableCell>
                      <div className="font-semibold">{a.asset_names}</div>
                      <div className="text-xs text-muted-foreground">{a.asset_count} bene/i</div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground text-center">{a.category_names || "—"}</TableCell>
                    <TableCell className="text-center">{a.employee_name}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md ${isConsegna ? "text-primary bg-primary/10" : "text-amber-700 bg-amber-100"}`} data-testid={`verbale-kind-${a.id}-${r.kind}`}>
                        {isConsegna ? <ClipboardSignature className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        {isConsegna ? "Consegna" : "Restituzione"}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-center text-muted-foreground">{r.date ? fmtDateTime(r.date) : "—"}</TableCell>
                    <TableCell className="text-center">
                      <SignChip s={isConsegna ? consegnaStatus(a) : restituzioneStatus(a)} testid={`${r.kind}-status-${a.id}`} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2 flex-wrap min-w-[220px]">
                        {isConsegna ? (
                          <>
                            <Button variant="outline" size="sm" onClick={() => openPdf(a.id, "pdf")} data-testid={`pdf-${a.id}`}>
                              <FileDown className="h-4 w-4 mr-1" /> Scarica
                            </Button>
                            {isAdmin && !a.has_signature && !a.has_signed_document && a.status === "attivo" && (
                              <Button variant="ghost" size="sm" onClick={() => onSign(a)} data-testid={`sign-later-${a.id}`}>
                                <PenLine className="h-4 w-4 mr-1" /> Firma tavoletta
                              </Button>
                            )}
                            {isAdmin && (a.has_signed_document ? (
                              <Button variant="ghost" size="sm" onClick={() => onRemoveDoc(a)} data-testid={`remove-signed-doc-${a.id}`}>
                                <Trash2 className="h-4 w-4 mr-1 text-destructive" /> Rimuovi PDF
                              </Button>
                            ) : !a.has_signature && (
                              <Button variant="ghost" size="sm" onClick={() => onUploadDoc(a)} data-testid={`upload-signed-doc-${a.id}`}>
                                <Upload className="h-4 w-4 mr-1" /> Carica firmato
                              </Button>
                            ))}
                            {isAdmin && a.status === "attivo" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => onEdit(a)} data-testid={`edit-assignment-${a.id}`}>
                                  <Pencil className="h-4 w-4 mr-1" /> Modifica
                                </Button>
                                {(a.has_signature || a.has_signed_document) && (
                                  <Button variant="ghost" size="sm" onClick={() => onReset(a)} data-testid={`reset-signature-${a.id}`}>
                                    <RefreshCw className="h-4 w-4 mr-1" /> Azzera firma
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => openReturn(a)} data-testid={`return-btn-${a.id}`}>
                                  <RotateCcw className="h-4 w-4 mr-1" /> Riconsegna
                                </Button>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Button variant="outline" size="sm" onClick={() => openPdf(a.id, "return-pdf")} data-testid={`return-pdf-${a.id}`}>
                              <FileDown className="h-4 w-4 mr-1" /> Scarica
                            </Button>
                            {isAdmin && (a.has_return_signed_document ? (
                              <Button variant="ghost" size="sm" onClick={() => onRemoveReturnDoc(a)} data-testid={`remove-return-signed-doc-${a.id}`}>
                                <Trash2 className="h-4 w-4 mr-1 text-destructive" /> Rimuovi PDF
                              </Button>
                            ) : !a.return_has_signature && (
                              <Button variant="ghost" size="sm" onClick={() => onUploadReturnDoc(a)} data-testid={`upload-return-signed-doc-${a.id}`}>
                                <Upload className="h-4 w-4 mr-1" /> Carica firmato
                              </Button>
                            ))}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <EmptyState icon={ClipboardSignature} title="Nessuna assegnazione" subtitle="Nessun risultato con i filtri selezionati o nessuna assegnazione registrata." />
      )}
    </Card>
  );
}

export default function Assignments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [selEmp, setSelEmp] = useState(null);
  const [heldSort, setHeldSort] = useState({ key: "name", dir: "asc" });
  const [heldFilters, setHeldFilters] = useState({ name: [], category_name: [], identifier: [] });
  // new assignment
  const [nOpen, setNOpen] = useState(false);
  const [selUser, setSelUser] = useState("");
  const [selAssets, setSelAssets] = useState([""]);
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState("");
  const [assignedDate, setAssignedDate] = useState("");
  const [skipSig, setSkipSig] = useState(false);
  const [editAssignId, setEditAssignId] = useState(null);
  const [step, setStep] = useState(1);
  const [hasSigned, setHasSigned] = useState(false);
  const sigRef = useRef(null);
  // sign later + signed doc upload
  const [signOpen, setSignOpen] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const signSigRef = useRef(null);
  const signedDocInputRef = useRef(null);
  const signedDocTarget = useRef(null);
  // upload signed verbale (with signer)
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadKind, setUploadKind] = useState("consegna");
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadSignedBy, setUploadSignedBy] = useState("");
  const [uploadPath, setUploadPath] = useState("");
  const [uploadName, setUploadName] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const uploadInputRef = useRef(null);
  // return
  const [rOpen, setROpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [operatorName, setOperatorName] = useState(user?.name || "");
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [returnAssetIds, setReturnAssetIds] = useState([]);
  const [returnDate, setReturnDate] = useState("");
  const [skipReturnSig, setSkipReturnSig] = useState(false);
  const [returnSignedPath, setReturnSignedPath] = useState("");
  const [returnSignedName, setReturnSignedName] = useState("");
  const [returnDocBusy, setReturnDocBusy] = useState(false);
  const returnDocInputRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const damageInputRef = useRef(null);
  const opSigRef = useRef(null);
  const empSigRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/assignments")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
    api.get("/assets").then(({ data }) => setAssets(data));
    api.get("/users").then(({ data }) => setEmployees(data.filter((u) => u.role === "employee" || u.role === "admin")));
  };
  useEffect(load, []);

  const openPdf = async (id, kind = "pdf") => {
    try {
      const { data } = await api.get(`/assignments/${id}/${kind}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error("Impossibile aprire il verbale"); }
  };

  const availableAssets = assets.filter((a) => a.status === "disponibile" || (a.management_type === "quantita" && (a.available_qty || 0) > 0) || selAssets.includes(a.id));
  const rowOptions = (idx) => availableAssets.filter((a) => a.id === selAssets[idx] || !selAssets.includes(a.id));
  const setAssetAt = (idx, id) => setSelAssets((s) => s.map((v, i) => (i === idx ? id : v)));
  const assetById = (id) => assets.find((a) => a.id === id);
  const setQtyFor = (id, v) => setQuantities((q) => ({ ...q, [id]: v }));
  const addAssetRow = () => setSelAssets((s) => [...s, ""]);
  const removeAssetRow = (idx) => setSelAssets((s) => (s.length > 1 ? s.filter((_, i) => i !== idx) : s));

  const openNewAssign = () => { setEditAssignId(null); setStep(1); setSelUser(""); setSelAssets([""]); setQuantities({}); setNotes(""); setAssignedDate(nowRomeInput()); setSkipSig(false); setNOpen(true); };
  const openEditAssign = (a) => {
    setEditAssignId(a.id); setStep(1); setSelUser(a.user_id);
    setSelAssets((a.assets || []).map((x) => x.id).filter(Boolean).length ? a.assets.map((x) => x.id) : [""]);
    setQuantities(Object.fromEntries((a.assets || []).filter((x) => x.management_type === "quantita").map((x) => [x.id, x.qty || 1])));
    setNotes(a.notes || ""); setSkipSig(false); setNOpen(true);
  };

  const goToSign = () => {
    if (!selUser) { toast.error("Seleziona un dipendente"); return; }
    const chosen = selAssets.filter(Boolean);
    if (!chosen.length) { toast.error("Seleziona almeno un bene"); return; }
    for (const id of chosen) {
      const as = assetById(id);
      if (as?.management_type === "quantita") {
        const req = parseInt(quantities[id], 10) || 1;
        if (req < 1) { toast.error(`Quantità non valida per ${as.name}`); return; }
        if (req > (as.available_qty || 0)) { toast.error(`${as.name}: disponibili ${as.available_qty}, richiesti ${req}`); return; }
      }
    }
    setHasSigned(false); setStep(2);
  };

  const buildQtyPayload = (chosen) => {
    const out = {};
    for (const id of chosen) {
      const as = assetById(id);
      if (as?.management_type === "quantita") out[id] = Math.max(parseInt(quantities[id], 10) || 1, 1);
    }
    return out;
  };

  const saveAssignment = async () => {
    if (!selUser) { toast.error("Seleziona un dipendente"); return; }
    const chosen = selAssets.filter(Boolean);
    if (!chosen.length) { toast.error("Seleziona almeno un bene"); return; }
    const qtyPayload = buildQtyPayload(chosen);
    if (editAssignId) {
      setBusy(true);
      try {
        await api.put(`/assignments/${editAssignId}`, { asset_ids: chosen, user_id: selUser, signature: "", notes, quantities: qtyPayload });
        toast.success("Assegnazione aggiornata — nuovo verbale da rifirmare");
        setNOpen(false); setEditAssignId(null); load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setBusy(false); }
      return;
    }
    if (!skipSig && sigRef.current?.isEmpty()) { toast.error("Firma dell'assegnatario richiesta, oppure attiva \"Salta firma\""); return; }
    if (!assignedDate) { toast.error("Indica la data di consegna"); return; }
    setBusy(true);
    try {
      await api.post("/assignments", { asset_ids: chosen, user_id: selUser, signature: skipSig ? "" : sigRef.current.getData(), notes, assigned_date: `${assignedDate}:00`, quantities: qtyPayload });
      toast.success(skipSig ? "Assegnazione registrata senza firma" : "Assegnazione registrata");
      setNOpen(false); setSelUser(""); setSelAssets([""]); setNotes(""); setSkipSig(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openUploadDoc = (a, kind) => {
    setUploadTarget(a); setUploadKind(kind); setUploadPath(""); setUploadName("");
    if (kind === "consegna") setUploadSignedBy(a.employee_name || "");
    else setUploadSignedBy(`${a.employee_name || ""}${a.return_operator_name ? " e " + a.return_operator_name : ""}`.trim());
    setUploadOpen(true);
  };
  const onUploadFileChange = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Seleziona un file PDF"); return; }
    setUploadBusy(true);
    try { const up = await uploadFile(file); setUploadPath(up.path); setUploadName(file.name); toast.success("PDF pronto"); }
    catch { toast.error("Caricamento fallito"); }
    finally { setUploadBusy(false); }
  };
  const confirmUploadDoc = async () => {
    if (!uploadPath) { toast.error("Carica il PDF firmato"); return; }
    if (!uploadSignedBy.trim()) { toast.error("Indica chi ha firmato"); return; }
    setUploadBusy(true);
    try {
      const ep = uploadKind === "consegna" ? "signed-document" : "return-signed-document";
      await api.post(`/assignments/${uploadTarget.id}/${ep}`, { path: uploadPath, filename: uploadName, signed_by: uploadSignedBy });
      toast.success("Verbale firmato caricato");
      setUploadOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploadBusy(false); }
  };
  const removeReturnSignedDoc = async (a) => {
    try { await api.delete(`/assignments/${a.id}/return-signed-document`); toast.success("PDF firmato rimosso"); load(); }
    catch { toast.error("Rimozione fallita"); }
  };
  const signerOptions = uploadTarget ? (
    uploadKind === "consegna"
      ? [uploadTarget.employee_name].filter(Boolean)
      : [
          `${uploadTarget.employee_name || ""}${uploadTarget.return_operator_name ? " e " + uploadTarget.return_operator_name : ""}`.trim(),
          uploadTarget.employee_name,
          uploadTarget.return_operator_name,
        ].filter(Boolean)
  ) : [];

  const resetSignature = async (a) => {
    if (!window.confirm("Rigenerare il verbale? La firma attuale verrà azzerata e l'assegnazione dovrà essere rifirmata.")) return;
    try {
      await api.post(`/assignments/${a.id}/reset-signature`);
      toast.success("Verbale rigenerato — da rifirmare");
      load();
    } catch { toast.error("Operazione fallita"); }
  };

  const openSign = (a) => { setHasSigned(false); setSignTarget(a); setSignOpen(true); };
  const doSignLater = async () => {
    if (signSigRef.current?.isEmpty()) { toast.error("Apponi la firma"); return; }
    setBusy(true);
    try {
      await api.post(`/assignments/${signTarget.id}/signature`, { signature: signSigRef.current.getData() });
      toast.success("Firma salvata sul verbale");
      setSignOpen(false); setSignTarget(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pickSignedDoc = (a) => { signedDocTarget.current = a.id; signedDocInputRef.current?.click(); };
  const handleSignedDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !signedDocTarget.current) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      await api.post(`/assignments/${signedDocTarget.current}/signed-document`, { path: up.path, filename: file.name });
      toast.success("PDF firmato caricato");
      signedDocTarget.current = null; load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Caricamento fallito"); }
    finally { setBusy(false); }
  };
  const removeSignedDoc = async (a) => {
    try {
      await api.delete(`/assignments/${a.id}/signed-document`);
      toast.success("PDF firmato rimosso");
      load();
    } catch { toast.error("Rimozione fallita"); }
  };

  const addDamagePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    setPhotoBusy(true);
    try { const res = await uploadFile(file); setDamagePhotos((p) => [...p, res.path]); }
    catch { toast.error("Caricamento foto fallito"); }
    finally { setPhotoBusy(false); if (damageInputRef.current) damageInputRef.current.value = ""; }
  };

  const openReturn = (a) => {
    setCurrent(a);
    setOperatorName(user?.name || "");
    setDamagePhotos([]);
    setReturnAssetIds((a.assets || []).map((x) => x.id));
    setReturnDate(nowRomeInput());
    setSkipReturnSig(false);
    setReturnSignedPath("");
    setReturnSignedName("");
    setROpen(true);
  };
  const toggleReturnAsset = (id) =>
    setReturnAssetIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const handleReturnSignedDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Seleziona un file PDF"); return; }
    setReturnDocBusy(true);
    try {
      const up = await uploadFile(file);
      setReturnSignedPath(up.path); setReturnSignedName(file.name);
      toast.success("PDF firmato caricato");
    } catch { toast.error("Caricamento fallito"); }
    finally { setReturnDocBusy(false); }
  };
  const doReturn = async () => {
    if (!operatorName.trim()) { toast.error("Indica l'operatore POWERCOM"); return; }
    if (!returnAssetIds.length) { toast.error("Seleziona almeno un bene da restituire"); return; }
    if (!returnDate) { toast.error("Indica la data di restituzione"); return; }
    const hasSignedDoc = !!returnSignedPath;
    const padsVisible = !skipReturnSig && !hasSignedDoc;
    const opEmpty = opSigRef.current?.isEmpty() ?? true;
    const empEmpty = empSigRef.current?.isEmpty() ?? true;
    if (padsVisible && (opEmpty !== empEmpty)) {
      toast.error("Completa entrambe le firme, oppure lasciale vuote per firmare in seguito");
      return;
    }
    const useSig = padsVisible && !opEmpty && !empEmpty;
    const total = (current?.assets || []).length;
    const partial = returnAssetIds.length < total;
    setBusy(true);
    try {
      await api.post(`/assignments/${current.id}/return`, {
        operator_name: operatorName,
        signature_operator: useSig ? opSigRef.current.getData() : "",
        signature_employee: useSig ? empSigRef.current.getData() : "",
        damage_photos: damagePhotos,
        asset_ids: returnAssetIds,
        returned_date: `${returnDate}:00`,
        return_signed_pdf_path: returnSignedPath,
        return_signed_pdf_filename: returnSignedName,
        return_signed_by: hasSignedDoc ? `${current.employee_name || ""}${operatorName ? " e " + operatorName : ""}`.trim() : "",
        return_skipped: skipReturnSig,
      });
      toast.success(partial ? "Riconsegna parziale registrata" : "Riconsegna registrata");
      setROpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filtered = items.filter((a) =>
    [a.asset_names, a.employee_name].join(" ").toLowerCase().includes(q.toLowerCase()));

  const byEmployee = Object.values(
    items.reduce((acc, a) => {
      const key = a.user_id || a.employee_name;
      if (!acc[key]) acc[key] = { id: key, name: a.employee_name, items: [], active: 0 };
      acc[key].items.push(a);
      if (a.status === "attivo") acc[key].active += 1;
      return acc;
    }, {})
  ).sort((x, y) => (x.name || "").localeCompare(y.name || ""));
  const selected = byEmployee.find((e) => e.id === selEmp);

  const heldRows = selected
    ? [...new Map(
        selected.items
          .filter((i) => i.status === "attivo")
          .flatMap((i) => (i.assets || []).map((x) => [x.id, { ...x, assigned_date: i.assigned_date }]))
      ).values()]
    : [];

  const renderHeldTable = () => {
    const opt = (fn) => [...new Set(heldRows.map(fn))].filter(Boolean).sort();
    const match = (arr, v) => arr.length === 0 || arr.includes(v);
    const rows = heldRows
      .filter((x) =>
        match(heldFilters.name, x.name) &&
        match(heldFilters.category_name, x.category_name || "—") &&
        match(heldFilters.identifier, x.identifier || "—"))
      .sort((a, b) => {
        const k = heldSort.key;
        const va = ((a[k] || "") + "").toLowerCase();
        const vb = ((b[k] || "") + "").toLowerCase();
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return heldSort.dir === "asc" ? cmp : -cmp;
      });
    const toggleSort = (key) => setHeldSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
    const SortIcon = ({ k }) => heldSort.key !== k ? <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" /> : heldSort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5 text-primary" /> : <ChevronDown className="h-3.5 w-3.5 text-primary" />;
    const Th = ({ k, children, filter, className = "" }) => (
      <TableHead className={`label-caps ${className}`}>
        <div className="flex items-center gap-1">
          <button className="flex items-center gap-1 hover:text-primary" onClick={() => toggleSort(k)} data-testid={`held-sort-${k}`}>
            {children} <SortIcon k={k} />
          </button>
          {filter}
        </div>
      </TableHead>
    );
    return (
      <Card className="rounded-lg overflow-hidden mb-4" data-testid="employee-held-assets">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="label-caps text-muted-foreground">Beni attualmente in possesso ({heldRows.length})</div>
          {heldRows.length > 0 && (
            <Button variant="outline" size="sm" className="font-semibold"
              onClick={() => downloadBlob(`/export/held-assets?user_id=${selected.id}`, `beni_in_possesso_${selected.name}.xlsx`)}
              data-testid="export-held-assets-button">
              <FileDown className="h-4 w-4 mr-1.5" /> Esporta beni
            </Button>
          )}
        </div>
        {rows.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  <Th k="name" filter={<MultiFilter label="Bene" options={opt((x) => x.name)} value={heldFilters.name} onChange={(v) => setHeldFilters({ ...heldFilters, name: v })} testid="held-filter-name" />}>Bene</Th>
                  <Th k="category_name" filter={<MultiFilter label="Categoria" options={opt((x) => x.category_name || "—")} value={heldFilters.category_name} onChange={(v) => setHeldFilters({ ...heldFilters, category_name: v })} testid="held-filter-category" />}>Categoria</Th>
                  <Th k="identifier" className="hidden sm:table-cell" filter={<MultiFilter label="Matricola/Targa" options={opt((x) => x.identifier || "—")} value={heldFilters.identifier} onChange={(v) => setHeldFilters({ ...heldFilters, identifier: v })} testid="held-filter-identifier" />}>Matricola/Targa</Th>
                  <Th k="assigned_date" className="hidden md:table-cell">Consegna</Th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((x) => (
                  <TableRow key={x.id} data-testid={`held-row-${x.id}`} className="cursor-pointer" onClick={() => navigate(`/beni/${x.id}`)}>
                    <TableCell className="font-semibold">{x.name}</TableCell>
                    <TableCell className="text-muted-foreground">{x.category_name || "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell font-mono text-sm">{x.identifier || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{fmtDateTime(x.assigned_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState icon={Package} title="Nessun bene attualmente assegnato" subtitle="Questo dipendente non ha beni attivi in possesso." />
        )}
      </Card>
    );
  };

  return (
    <div>
      <PageHeader eyebrow="Consegne" title="Assegnazioni" subtitle="Consegne e riconsegne con verbali firmati">
        {isAdmin && (
          <Button onClick={openNewAssign} data-testid="new-assignment-button" className="font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova assegnazione
          </Button>
        )}
      </PageHeader>

      <Tabs defaultValue="crono">
        <TabsList>
          <TabsTrigger value="crono" data-testid="tab-crono">Cronologico</TabsTrigger>
          <TabsTrigger value="per-dipendente" data-testid="tab-per-dipendente">Per dipendente ({byEmployee.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="crono" className="mt-4">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="relative max-w-md flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="assignment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per bene o dipendente…" className="pl-9" />
            </div>
            <Button variant="outline" className="font-semibold" onClick={() => downloadBlob("/export/assignments", "assegnazioni.xlsx")} data-testid="export-assignments-button">
              <FileDown className="h-4 w-4 mr-1.5" /> Esporta XLSX
            </Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="assignments-loading">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento movimenti…
            </div>
          ) : (
            <AssignmentTable rows={filtered} isAdmin={isAdmin} openPdf={openPdf} openReturn={openReturn} onSign={openSign} onUploadDoc={(a) => openUploadDoc(a, "consegna")} onRemoveDoc={removeSignedDoc} onUploadReturnDoc={(a) => openUploadDoc(a, "restituzione")} onRemoveReturnDoc={removeReturnSignedDoc} onEdit={openEditAssign} onReset={resetSignature} />
          )}
        </TabsContent>

        <TabsContent value="per-dipendente" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="employees-loading">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento movimenti…
            </div>
          ) : selected ? (
            <div>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" onClick={() => setSelEmp(null)} data-testid="back-to-employees" className="-ml-2">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Tutti i dipendenti
                  </Button>
                  <div>
                    <div className="font-heading font-bold text-xl">{selected.name}</div>
                    <div className="text-xs text-muted-foreground">{selected.items.length} assegnazione/i · {selected.active} attiva/e</div>
                  </div>
                </div>
                <Button variant="outline" className="font-semibold" onClick={() => downloadBlob(`/export/assignments?user_id=${selected.id}`, `assegnazioni_${selected.name}.xlsx`)} data-testid="export-employee-assignments-button">
                  <FileDown className="h-4 w-4 mr-1.5" /> Esporta assegnazioni
                </Button>
              </div>
              {renderHeldTable()}
              <AssignmentTable rows={selected.items} isAdmin={isAdmin} openPdf={openPdf} openReturn={openReturn} onSign={openSign} onUploadDoc={(a) => openUploadDoc(a, "consegna")} onRemoveDoc={removeSignedDoc} onUploadReturnDoc={(a) => openUploadDoc(a, "restituzione")} onRemoveReturnDoc={removeReturnSignedDoc} onEdit={openEditAssign} onReset={resetSignature} />
            </div>
          ) : byEmployee.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {byEmployee.map((e) => (
                <Card key={e.id} data-testid={`employee-card-${e.id}`} onClick={() => setSelEmp(e.id)}
                  className="p-5 rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-lg truncate">{e.name}</div>
                      <div className="text-xs text-muted-foreground">{e.items.length} assegnazione/i</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="label-caps px-2 py-0.5 rounded-md bg-primary/10 text-primary">{e.active} attiva/e</span>
                    <span className="label-caps px-2 py-0.5 rounded-md bg-muted text-muted-foreground">{e.items.length - e.active} chiusa/e</span>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="rounded-lg"><EmptyState icon={Users2} title="Nessun dipendente con assegnazioni" subtitle="Le assegnazioni raggruppate per dipendente compariranno qui." /></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* New assignment */}
      <Dialog open={nOpen} onOpenChange={setNOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editAssignId ? "Modifica assegnazione" : "Nuova assegnazione"}</DialogTitle>
            <DialogDescription>{editAssignId ? "Modifica dipendente, beni e note. La firma verrà azzerata: il nuovo verbale dovrà essere rifirmato." : "Assegna uno o più beni a un dipendente con firma di presa in carico."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(editAssignId || step === 1) ? (
              <>
                <div className="space-y-1.5">
                  <Label>Dipendente assegnatario *</Label>
                  <Select value={selUser} onValueChange={setSelUser}>
                    <SelectTrigger data-testid="assignment-user-select"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.codice_fiscale ? ` — ${u.codice_fiscale}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Beni da assegnare * <span className="text-muted-foreground font-normal">(nessun limite)</span></Label>
                  <div className="space-y-2" data-testid="assignment-assets-list">
                    {selAssets.map((sel, idx) => {
                      const selAsset = assetById(sel);
                      const isQty = selAsset?.management_type === "quantita";
                      return (
                      <div key={idx} className="flex items-center gap-2">
                        <Select value={sel || undefined} onValueChange={(v) => setAssetAt(idx, v)}>
                          <SelectTrigger data-testid={`assignment-asset-select-${idx}`} className="flex-1">
                            <SelectValue placeholder="Seleziona un bene" />
                          </SelectTrigger>
                          <SelectContent>
                            {rowOptions(idx).length ? rowOptions(idx).map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.name}{a.management_type === "quantita" ? ` — disp. ${a.available_qty}/${a.quantity}` : (a.identifier ? ` — ${a.identifier}` : ` — ${a.category_name || ""}`)}
                              </SelectItem>
                            )) : <div className="px-3 py-2 text-sm text-muted-foreground">Nessun altro bene disponibile</div>}
                          </SelectContent>
                        </Select>
                        {isQty && (
                          <Input type="number" min="1" max={selAsset.available_qty}
                            value={quantities[sel] ?? 1}
                            onChange={(e) => setQtyFor(sel, e.target.value)}
                            className="w-20" data-testid={`assignment-qty-input-${idx}`} title={`Disponibili: ${selAsset.available_qty}`} />
                        )}
                        {selAssets.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeAssetRow(idx)} data-testid={`remove-asset-row-${idx}`}>
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addAssetRow}
                    disabled={selAssets.some((v) => !v) || selAssets.filter(Boolean).length >= availableAssets.length}
                    data-testid="add-asset-row-button" className="mt-1">
                    <Plus className="h-4 w-4 mr-1.5" /> Assegna altro
                  </Button>
                </div>
                <div className="space-y-1.5">
                  <Label>Data e ora di consegna *</Label>
                  <Input type="datetime-local" data-testid="assignment-date-input" value={assignedDate} onChange={(e) => setAssignedDate(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Note (opzionale)</Label>
                  <Input data-testid="assignment-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                {editAssignId ? (
                  <div className="rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">Salvando le modifiche, la firma precedente verrà azzerata. Scarica il nuovo verbale di consegna e fallo rifirmare (pulsante "Firma") oppure carica il PDF firmato.</p>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-border p-3">
                    <Checkbox checked={skipSig} onCheckedChange={(v) => setSkipSig(!!v)} data-testid="skip-signature-checkbox" />
                    <span className="text-sm font-medium">Salta firma (malfunzionamento dispositivo)</span>
                  </label>
                )}
              </>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg bg-secondary/50 p-3 text-sm">
                  <div><span className="text-muted-foreground">Assegnatario: </span><span className="font-semibold">{employees.find((u) => u.id === selUser)?.name || "—"}</span></div>
                  <div className="mt-1"><span className="text-muted-foreground">Beni: </span><span className="font-semibold">{selAssets.filter(Boolean).map((aid) => assets.find((x) => x.id === aid)?.name).filter(Boolean).join(", ")}</span></div>
                </div>
                <div className="space-y-1.5">
                  <Label>Firma dell'assegnatario (tavoletta grafica)</Label>
                  <p className="text-xs text-muted-foreground">Tocca il riquadro (o "Firma a schermo intero") per aprire l'area di firma a tutto schermo: la penna della tavoletta è mappata sull'intero schermo, così puoi firmare comodamente senza uscire dal riquadro. Poi tocca "Conferma firma".</p>
                  <SignaturePad ref={sigRef} label="Firma qui" onChange={setHasSigned} />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {editAssignId ? (
              <>
                <Button variant="outline" onClick={() => setNOpen(false)}>Annulla</Button>
                <Button onClick={saveAssignment} disabled={busy} data-testid="confirm-new-assignment-button">{busy ? "Salvataggio…" : "Salva modifiche"}</Button>
              </>
            ) : step === 1 ? (
              <>
                <Button variant="outline" onClick={() => setNOpen(false)}>Annulla</Button>
                {skipSig ? (
                  <Button onClick={saveAssignment} disabled={busy} data-testid="confirm-new-assignment-button">{busy ? "Salvataggio…" : "Assegna senza firma"}</Button>
                ) : (
                  <Button onClick={goToSign} data-testid="assignment-next-button">Avanti: firma <ChevronRight className="h-4 w-4 ml-1" /></Button>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setStep(1)} data-testid="assignment-back-button"><ChevronLeft className="h-4 w-4 mr-1" /> Indietro</Button>
                <Button onClick={saveAssignment} disabled={busy || !hasSigned} data-testid="confirm-new-assignment-button">{busy ? "Salvataggio…" : "Firma e assegna"}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Firma in un secondo momento */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Firma verbale di consegna</DialogTitle>
            <DialogDescription>{signTarget?.asset_names} — {signTarget?.employee_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Firma dell'assegnatario</Label>
            <SignaturePad ref={signSigRef} label="Firma qui" onChange={setHasSigned} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>Annulla</Button>
            <Button onClick={doSignLater} disabled={busy || !hasSigned} data-testid="confirm-sign-later-button">{busy ? "Salvataggio…" : "Salva firma"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input ref={signedDocInputRef} type="file" accept="application/pdf" onChange={handleSignedDoc} className="hidden" data-testid="signed-doc-input" />

      {/* Return */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Riconsegna</DialogTitle>
            <DialogDescription>{current?.asset_names} — {current?.employee_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Beni da restituire *</Label>
              <p className="text-xs text-muted-foreground">Seleziona i beni effettivamente riconsegnati. Se ne restituisci solo alcuni, l'assegnazione resta attiva per i beni rimanenti.</p>
              <div className="space-y-1.5 rounded-lg border border-border p-3" data-testid="return-assets-list">
                {(current?.assets || []).map((x) => (
                  <label key={x.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={returnAssetIds.includes(x.id)}
                      onCheckedChange={() => toggleReturnAsset(x.id)}
                      data-testid={`return-asset-checkbox-${x.id}`}
                    />
                    <span className="font-medium">{x.name}{x.management_type === "quantita" ? ` × ${x.qty}` : ""}</span>
                    {x.management_type !== "quantita" && x.identifier && <span className="text-muted-foreground">— {x.identifier}</span>}
                    <span className="text-muted-foreground">· {x.category_name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Operatore POWERCOM *</Label>
              <Input data-testid="return-operator-input" value={operatorName} onChange={(e) => setOperatorName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Data e ora di restituzione *</Label>
              <Input type="datetime-local" data-testid="return-date-input" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-border p-3">
              <Checkbox checked={skipReturnSig} onCheckedChange={(v) => setSkipReturnSig(!!v)} data-testid="return-skip-signature-checkbox" />
              <span className="text-sm font-medium">Salta firma (malfunzionamento dispositivo)</span>
            </label>
            <div className="space-y-1.5">
              <Label>Verbale di restituzione firmato in olografo <span className="text-muted-foreground font-normal">(opzionale)</span></Label>
              <input ref={returnDocInputRef} type="file" accept="application/pdf" onChange={handleReturnSignedDoc} className="hidden" data-testid="return-signed-doc-input" />
              {returnSignedPath ? (
                <div className="flex items-center gap-2 rounded-lg border border-border p-2 text-sm" data-testid="return-signed-doc-info">
                  <FileCheck2 className="h-4 w-4 text-emerald-600" />
                  <span className="flex-1 truncate">{returnSignedName || "documento.pdf"}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setReturnSignedPath(""); setReturnSignedName(""); }} data-testid="return-remove-signed-doc">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" disabled={returnDocBusy}
                  onClick={() => returnDocInputRef.current?.click()} data-testid="return-upload-signed-doc">
                  {returnDocBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  Carica PDF firmato
                </Button>
              )}
            </div>
            {!skipReturnSig && !returnSignedPath && (
              <>
                <div className="space-y-1.5">
                  <Label>Firma per POWERCOM</Label>
                  <SignaturePad ref={opSigRef} label="Firma operatore" />
                </div>
                <div className="space-y-1.5">
                  <Label>Firma dell'assegnatario che riconsegna</Label>
                  <SignaturePad ref={empSigRef} label="Firma assegnatario" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Foto danni / stato del bene <span className="text-muted-foreground font-normal">(opzionale, allegate al verbale)</span></Label>
              <input ref={damageInputRef} type="file" accept="image/*" onChange={addDamagePhoto} className="hidden" data-testid="damage-photo-input" />
              <div className="flex items-center gap-3 flex-wrap">
                {damagePhotos.map((p, i) => (
                  <div key={i} className="relative h-20 w-20 rounded-md border border-border overflow-hidden">
                    <img src={fileUrl(p)} alt={`Danno ${i + 1}`} className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setDamagePhotos((arr) => arr.filter((_, j) => j !== i))}
                      data-testid={`remove-damage-photo-${i}`}
                      className="absolute top-0.5 right-0.5 bg-background/90 rounded-full p-0.5 border border-border">
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" disabled={photoBusy}
                  onClick={() => damageInputRef.current?.click()} data-testid="add-damage-photo-button">
                  {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1.5" />}
                  Aggiungi foto
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setROpen(false)}>Annulla</Button>
            <Button onClick={doReturn} disabled={busy} data-testid="confirm-return-button">{busy ? "Salvataggio…" : "Conferma riconsegna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload signed verbale (with signer) */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Carica verbale firmato a mano</DialogTitle>
            <DialogDescription>
              {uploadKind === "consegna" ? "Verbale di consegna" : "Verbale di restituzione"} — {uploadTarget?.asset_names}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Firmato da *</Label>
              <Select value={uploadSignedBy} onValueChange={setUploadSignedBy}>
                <SelectTrigger data-testid="upload-signed-by-select"><SelectValue placeholder="Seleziona chi ha firmato" /></SelectTrigger>
                <SelectContent>
                  {signerOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>File PDF firmato *</Label>
              <input ref={uploadInputRef} type="file" accept="application/pdf" onChange={onUploadFileChange} className="hidden" data-testid="upload-doc-file-input" />
              {uploadPath ? (
                <div className="flex items-center gap-2 rounded-lg border border-border p-2 text-sm" data-testid="upload-doc-info">
                  <FileCheck2 className="h-4 w-4 text-emerald-600" />
                  <span className="flex-1 truncate">{uploadName || "documento.pdf"}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setUploadPath(""); setUploadName(""); }} data-testid="upload-doc-clear">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" disabled={uploadBusy}
                  onClick={() => uploadInputRef.current?.click()} data-testid="upload-doc-pick">
                  {uploadBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  Scegli PDF
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Il PDF firmato sostituirà il verbale scaricabile e lo stato passerà a "Firmato a mano".</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annulla</Button>
            <Button onClick={confirmUploadDoc} disabled={uploadBusy} data-testid="confirm-upload-doc-button">{uploadBusy ? "Salvataggio…" : "Carica e chiudi firma"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
